import React, { useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";
import SaveOutlinedIcon from "@material-ui/icons/SaveOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import GetAppOutlinedIcon from "@material-ui/icons/GetAppOutlined";
import LabeledTextField from "../../calc/shared/ui/HaklaiLabeledTextField";
import HelpDialog from "../../../layout/HelpDialog";
import useHaklai2990Panel from "./useHaklai2990Panel";
import {
  HAKLAI_2990_ACTIONS,
  HAKLAI_2990_FIELD_LABELS,
  HAKLAI_2990_FIELD_TOOLTIPS,
  HAKLAI_2990_LISHKAT_RISHUM_OPTIONS,
  HAKLAI_2990_LABELS,
  HAKLAI_2990_MESSAGES,
  HAKLAI_2990_SMART_ADDRESS_HELP,
  getHaklai2990ParcelChoiceLabel
} from "./haklai2990Texts";

/**
 * Owns the editable Haklai 2990 result panel.
 *
 * Responsibilities:
 * - renders generated form fields from analysis data
 * - coordinates parcel lookup, save, and document download controls
 * - delegates side effects and payload building to `useHaklai2990Panel`
 */

const SMART_ADDRESS_HELP_DIALOG = (
  <Box textAlign="right">
    <Typography variant="body2" component="div" gutterBottom>
      {HAKLAI_2990_SMART_ADDRESS_HELP.formatsTitle}
    </Typography>
    <ul style={{ margin: "8px 0 0", paddingRight: "18px" }}>
      {HAKLAI_2990_SMART_ADDRESS_HELP.formats.map((format) => (
        <li key={format}>{format}</li>
      ))}
    </ul>
    <Typography variant="body2" component="div" style={{ marginTop: 12 }}>
      {HAKLAI_2990_SMART_ADDRESS_HELP.examplesTitle}
      {HAKLAI_2990_SMART_ADDRESS_HELP.examples.map((example) => (
        <React.Fragment key={example}>
          <br />
          {example}
        </React.Fragment>
      ))}
    </Typography>
  </Box>
);

const FRACTION_LABEL_WIDTH = 48;
const FRACTION_INPUT_WIDTH = 150;
const FRACTION_BAR_WIDTH = 180;
const FRACTION_FIELD_GAP = 12;
const FRACTION_ROW_WIDTH = FRACTION_LABEL_WIDTH + FRACTION_BAR_WIDTH + FRACTION_FIELD_GAP;
const FRACTION_FRAME_HORIZONTAL_PADDING = 16;
const FRACTION_FRAME_WIDTH = FRACTION_ROW_WIDTH + FRACTION_FRAME_HORIZONTAL_PADDING * 2;
const PROPERTY_FIELD_ROWS = [
  [
    { type: "number", label: HAKLAI_2990_FIELD_LABELS.blockNum, name: "blockNum", noComma: true, maxLength: "6", textFieldWidth: 190 },
    { type: "number", label: HAKLAI_2990_FIELD_LABELS.subBlock, name: "subBlock", noComma: true, maxLength: "4", textFieldWidth: 190 },
    { type: "number", label: HAKLAI_2990_FIELD_LABELS.subSubBlock, name: "subSubBlock", noComma: true, maxLength: "3", textFieldWidth: 190 },
    { type: "number", label: HAKLAI_2990_FIELD_LABELS.assetZipcode, name: "assetZipcode", noComma: true, maxLength: "7", textFieldWidth: 190 }
  ],
  [
    { type: "text", label: HAKLAI_2990_FIELD_LABELS.assetCity, name: "assetCity", textFieldWidth: 190 },
    { type: "text", label: HAKLAI_2990_FIELD_LABELS.assetStreet, name: "assetStreet", textFieldWidth: 190 },
    { type: "number", label: HAKLAI_2990_FIELD_LABELS.assetStreetNumber, name: "assetStreetNumber", noComma: true, maxLength: "5", textFieldWidth: 190 },
    { type: "number", label: HAKLAI_2990_FIELD_LABELS.assetHouseNum, name: "assetHouseNum", noComma: true, maxLength: "4", textFieldWidth: 190 }
  ]
];
const SELLER_FIELD_ROWS = [
  [
    {
      type: "select",
      label: HAKLAI_2990_FIELD_LABELS.lishcatRishumLocation,
      name: "lishcatRishumLocation",
      labelWidth: 220,
      textFieldWidth: 220,
      useOfficeOptions: true
    },
    {
      type: "text",
      label: HAKLAI_2990_FIELD_LABELS.sellerFirstName,
      name: "sellerFirstName",
      labelWidth: 220,
      maxLength: "17",
      textFieldWidth: 220
    }
  ],
  [
    {
      type: "text",
      label: HAKLAI_2990_FIELD_LABELS.sellerLastName,
      name: "sellerLastName",
      labelWidth: 220,
      maxLength: "17",
      textFieldWidth: 220
    },
    {
      type: "number",
      label: HAKLAI_2990_FIELD_LABELS.sellerId,
      name: "sellerId",
      labelWidth: 220,
      noComma: true,
      sequencesOfZerosAllowed: true,
      maxLength: "9",
      textFieldWidth: 220,
      tooltip: HAKLAI_2990_FIELD_TOOLTIPS.sellerId
    }
  ]
];

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 20,
    border: "1px solid rgba(28, 64, 97, 0.12)",
    boxShadow: "0 12px 30px rgba(16, 42, 67, 0.08)",
    backgroundColor: "#ffffff"
  },
  cardContent: {
    padding: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2)
    },
    "&:last-child": {
      paddingBottom: theme.spacing(3)
    }
  },
  headerRow: {
    margin: theme.spacing(-3, -3, 2.5, -3),
    padding: theme.spacing(1.1, 3),
    borderRadius: "20px 20px 0 0",
    backgroundColor: "#34748c",
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(-2, -2, 2.5, -2),
      padding: theme.spacing(1.1, 2)
    }
  },
  titleBlock: {
    minWidth: 0
  },
  title: {
    fontWeight: 800,
    color: "#ffffff",
    lineHeight: 1.2
  },
  section: {
    marginTop: theme.spacing(2.5)
  },
  sectionPanel: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2.25),
    borderRadius: 18,
    border: "1px solid rgba(23, 50, 77, 0.09)",
    backgroundColor: "#fdfefe"
  },
  propertyDetailsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridTemplateAreas: `
      "lookup"
      "fields"
    `,
    gap: theme.spacing(2.25),
    [theme.breakpoints.up("xl")]: {
      direction: "ltr",
      gridTemplateColumns: "minmax(0, 1fr) minmax(480px, 520px)",
      gridTemplateAreas: `"fields lookup"`,
      alignItems: "stretch"
    }
  },
  sellerDetailsGrid: {
    display: "flex",
    flexWrap: "wrap",
    direction: "rtl",
    alignItems: "stretch",
    justifyContent: "flex-start",
    gap: theme.spacing(2.25)
  },
  lookupDetailsBlock: {
    gridArea: "lookup",
    direction: "rtl",
    padding: theme.spacing(1.75),
    borderRadius: 14,
    border: "1px solid rgba(42, 111, 151, 0.16)",
    background: "linear-gradient(180deg, rgba(247, 251, 254, 1) 0%, rgba(243, 248, 252, 1) 100%)",
    boxSizing: "border-box",
    width: "min(100%, 560px)",
    justifySelf: "start",
    [theme.breakpoints.up("xl")]: {
      width: "100%",
      justifySelf: "stretch"
    },
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center"
    }
  },
  propertyFieldsBlock: {
    gridArea: "fields",
    direction: "rtl",
    minWidth: 0
  },
  sellerFieldsBlock: {
    direction: "rtl",
    flex: "0 1 456px",
    maxWidth: "100%",
    minWidth: 0,
    marginLeft: theme.spacing(5),
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0
    }
  },
  sellerFieldRows: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(1.5)
  },
  sellerFieldLine: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    gap: theme.spacing(2)
  },
  sellerFieldCell: {
    flex: "0 0 220px",
    width: 220,
    maxWidth: "100%",
    minWidth: 0,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      flexBasis: "100%"
    }
  },
  fractionDetailsBlock: {
    direction: "rtl",
    flex: `0 0 ${FRACTION_FRAME_WIDTH}px`,
    width: FRACTION_FRAME_WIDTH,
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(1.75, 2),
    borderRadius: 12,
    border: "1px solid rgba(23, 50, 77, 0.16)",
    backgroundColor: "#fbfdff",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    alignSelf: "center",
    [theme.breakpoints.down("xs")]: {
      flexBasis: "100%",
      width: "100%",
      justifyContent: "center"
    }
  },
  fractionFrameContent: {
    width: FRACTION_ROW_WIDTH,
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(1.75)
  },
  fractionTitle: {
    fontWeight: 700,
    color: "#17324d",
    textAlign: "right",
    lineHeight: 1.2
  },
  sectionTitle: {
    fontWeight: 800,
    color: "#17324d",
    marginBottom: 0
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5),
    color: "#17324d"
  },
  lookupHeaderIcon: {
    fontSize: "1rem",
    color: "#2d5f84"
  },
  lookupRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 260px) auto",
    alignItems: "start",
    columnGap: theme.spacing(2.75),
    rowGap: theme.spacing(1.25),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  lookupRowWithInfo: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 260px) auto",
    alignItems: "start",
    columnGap: theme.spacing(2.75),
    rowGap: theme.spacing(1.25),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  searchFieldWrap: {
    width: "100%"
  },
  lookupInputRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center"
    }
  },
  lookupFieldStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    [theme.breakpoints.down("sm")]: {
      alignItems: "center"
    }
  },
  lookupHelpIconWrap: {
    paddingTop: theme.spacing(0.75),
    flex: "0 0 auto"
  },
  lookupInfoBox: {
    gridColumn: "1 / -1",
    alignSelf: "start",
    color: "#5c7385",
    lineHeight: 1.45,
    paddingTop: theme.spacing(0.75),
    [theme.breakpoints.down("sm")]: {
      alignSelf: "center",
      textAlign: "center"
    },
    [theme.breakpoints.down("xs")]: {
      alignSelf: "stretch"
    }
  },
  lookupButton: {
    minWidth: 126,
    height: 36,
    whiteSpace: "nowrap",
    alignSelf: "start",
    justifySelf: "start",
    width: "auto",
    [theme.breakpoints.down("sm")]: {
      justifySelf: "center"
    }
  },
  lookupError: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1.25),
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: {
      textAlign: "center"
    }
  },
  inlineError: {
    color: theme.palette.error.main,
    fontWeight: 600
  },
  lookupMessageSlot: {
    minHeight: theme.spacing(5),
    display: "flex",
    alignItems: "flex-start",
    paddingTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      textAlign: "center"
    }
  },
  lookupMessagePlaceholder: {
    visibility: "hidden"
  },
  gridSection: {
    marginTop: theme.spacing(0.5)
  },
  fieldRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2)
  },
  fieldCell: {
    flex: "0 1 190px",
    maxWidth: "100%",
    [theme.breakpoints.down("xs")]: {
      flexBasis: "100%"
    }
  },
  fieldCellWide: {
    flex: "0 1 250px",
    maxWidth: "100%",
    [theme.breakpoints.down("xs")]: {
      flexBasis: "100%"
    }
  },
  searchFieldCell: {
    width: "100%",
    maxWidth: 340,
    [theme.breakpoints.down("sm")]: {
      justifySelf: "center",
      alignItems: "center"
    },
    [theme.breakpoints.down("xs")]: {
      maxWidth: "100%"
    }
  },
  fieldDivider: {
    margin: `${theme.spacing(2.25)}px 0`
  },
  fractionGrid: {
    width: FRACTION_ROW_WIDTH,
    maxWidth: "100%",
    display: "grid",
    direction: "rtl",
    gridTemplateColumns: `${FRACTION_LABEL_WIDTH}px ${FRACTION_BAR_WIDTH}px`,
    columnGap: FRACTION_FIELD_GAP,
    rowGap: theme.spacing(1.25),
    alignItems: "center"
  },
  fractionLabel: {
    fontWeight: 500,
    color: "#17324d",
    textAlign: "right",
    whiteSpace: "nowrap"
  },
  fractionInputSlot: {
    width: FRACTION_BAR_WIDTH,
    display: "flex",
    justifyContent: "center",
    "& > .MuiGrid-root": {
      width: "auto"
    },
    "& .MuiInputBase-input": {
      textAlign: "center"
    }
  },
  fractionSpacer: {
    width: FRACTION_LABEL_WIDTH
  },
  fractionBar: {
    width: FRACTION_BAR_WIDTH,
    maxWidth: "100%",
    margin: theme.spacing(0.5, 0),
    height: 3,
    borderRadius: 3,
    backgroundColor: "#17324d"
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5)
  },
  actionButton: {
    minWidth: 150,
    "& .MuiButton-label": {
      display: "inline-flex",
      alignItems: "center",
      lineHeight: 1
    },
    "& .MuiButton-endIcon": {
      marginRight: theme.spacing(1),
      marginLeft: 0
    }
  },
  exportButton: {
    minWidth: 140
  },
  jsonBlock: {
    marginTop: theme.spacing(3)
  },
  jsonGroupsGrid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    direction: "rtl",
    gap: theme.spacing(1.5)
  },
  jsonGroup: {
    width: "fit-content",
    minWidth: 190,
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(1.5),
    borderRadius: 16,
    backgroundColor: "rgba(255, 255, 255, 0.72)",
    border: "1px solid rgba(23, 50, 77, 0.08)",
    [theme.breakpoints.down("xs")]: {
      width: "100%"
    }
  },
  jsonGroupTitle: {
    fontWeight: 700,
    color: "#1a3650",
    marginBottom: theme.spacing(1),
    textAlign: "center",
    width: "100%"
  },
  jsonButtonsRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: theme.spacing(1)
  },
  jsonEmptyState: {
    marginTop: theme.spacing(1.25),
    color: "#61788b"
  },
  compactDivider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

const TransferredShareFraction = ({
  classes,
  disabled,
  docFields,
  fieldErrors,
  onChange,
  registerFieldRef
}) => (
  <Box className={classes.fractionDetailsBlock}>
    <Box className={classes.fractionFrameContent}>
      <Typography variant="subtitle2" className={classes.fractionTitle}>
        {HAKLAI_2990_LABELS.transferredShare}
      </Typography>
      <Box className={classes.fractionGrid}>
        <Typography variant="subtitle1" className={classes.fractionLabel}>
          {HAKLAI_2990_LABELS.numerator}
        </Typography>
        <Box
          className={classes.fractionInputSlot}
          ref={registerFieldRef("passPartMekarkeinMone")}
        >
          <LabeledTextField
            type="number"
            noComma
            maxLength="12"
            name="passPartMekarkeinMone"
            value={docFields.passPartMekarkeinMone}
            textFieldWidth={FRACTION_INPUT_WIDTH}
            labelSpacing={0}
            inputSpacing={0}
            noWrap
            disabled={disabled}
            error={Boolean(fieldErrors.passPartMekarkeinMone)}
            helperText={fieldErrors.passPartMekarkeinMone || ""}
            onChange={onChange}
          />
        </Box>
        <Box className={classes.fractionSpacer} />
        <Box className={classes.fractionBar} />
        <Typography variant="subtitle1" className={classes.fractionLabel}>
          {HAKLAI_2990_LABELS.denominator}
        </Typography>
        <Box
          className={classes.fractionInputSlot}
          ref={registerFieldRef("passPartMekarkeinMechane")}
        >
          <LabeledTextField
            type="number"
            noComma
            maxLength="12"
            name="passPartMekarkeinMechane"
            value={docFields.passPartMekarkeinMechane}
            textFieldWidth={FRACTION_INPUT_WIDTH}
            labelSpacing={0}
            inputSpacing={0}
            noWrap
            disabled={disabled}
            error={Boolean(fieldErrors.passPartMekarkeinMechane)}
            helperText={fieldErrors.passPartMekarkeinMechane || ""}
            onChange={onChange}
          />
        </Box>
      </Box>
    </Box>
  </Box>
);

const HaklaiResults2990Panel = ({
  dealId,
  haklaiDraftPreCalc,
  analyzedPreCalc
}) => {
  const classes = useStyles();
  const {
    activeJsonButton,
    addressChoices,
    addressMenuAnchor,
    addressSearchLoading,
    closeAddressParcelMenu,
    docFields,
    exportGroups,
    handleAddressChoiceClick,
    handleAddressKeyPress,
    handleAddressSearchClick,
    handleDocFieldChange,
    handleGenerateDeductions,
    handleGenerateJson,
    handleGeneratePdf,
    handleParcelSearchFieldChange,
    handleSave,
    hasGovMapAutofill,
    isDocumentActionBusy,
    isGeneratingDeductions,
    isGeneratingPdf,
    isLoading,
    isSaving,
    jsonFieldErrors,
    jsonValidationFocusRequest,
    loadError,
    parcelAddressError,
    parcelAddressSearch,
    parcelMenuAnchorRef,
    parcelPairError
  } = useHaklai2990Panel({
    analyzedPreCalc,
    dealId,
    haklaiDraftPreCalc
  });

  const fieldRefs = useRef({});
  const registerFieldRef = (fieldName) => (node) => {
    if (node) fieldRefs.current[fieldName] = node;
    else delete fieldRefs.current[fieldName];
  };

  useEffect(() => {
    const fieldName = jsonValidationFocusRequest?.fieldName;
    if (!fieldName) return;

    const fieldElement = fieldRefs.current[fieldName];
    if (!fieldElement) return;

    if (typeof fieldElement.scrollIntoView === "function") {
      fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const focusTarget = fieldElement.querySelector("input, textarea, button, [tabindex]");
    if (focusTarget && typeof focusTarget.focus === "function") {
      focusTarget.focus();
    }
  }, [jsonValidationFocusRequest]);

  const officeOptions = HAKLAI_2990_LISHKAT_RISHUM_OPTIONS;
  const fieldsDisabled = isLoading || isDocumentActionBusy;
  const renderDocField = (field, cellClassName) => {
    const {
      tooltip,
      useOfficeOptions,
      ...textFieldProps
    } = field;
    const fieldNode = (
      <Box
        key={field.name}
        className={cellClassName}
        ref={registerFieldRef(field.name)}
      >
        <LabeledTextField
          {...textFieldProps}
          options={useOfficeOptions ? officeOptions : field.options}
          value={docFields[field.name]}
          labeledTextFieldDirection="column"
          disabled={fieldsDisabled || textFieldProps.disabled}
          error={Boolean(jsonFieldErrors[field.name]) || textFieldProps.error}
          helperText={jsonFieldErrors[field.name] || textFieldProps.helperText}
          onChange={handleDocFieldChange}
        />
      </Box>
    );

    if (!tooltip) return fieldNode;

    return (
      <Tooltip key={field.name} title={tooltip} placement="bottom-start">
        {fieldNode}
      </Tooltip>
    );
  };

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.headerRow}>
          <Box className={classes.titleBlock}>
            <Typography variant="h6" className={classes.title}>
              {HAKLAI_2990_LABELS.panelTitle}
            </Typography>
          </Box>
        </Box>

        <Box className={`${classes.section} ${classes.sectionPanel}`}>
          <Box className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {HAKLAI_2990_LABELS.propertyDetails}
            </Typography>
          </Box>

          <Box className={classes.propertyDetailsGrid}>
            <Box className={classes.lookupDetailsBlock}>
              <Box className={classes.sectionHeader}>
                <RoomOutlinedIcon className={classes.lookupHeaderIcon} />
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  {HAKLAI_2990_LABELS.propertyLookup}
                </Typography>
              </Box>
              <Box className={hasGovMapAutofill ? classes.lookupRowWithInfo : classes.lookupRow}>
                <Box
                  ref={parcelMenuAnchorRef}
                  className={`${classes.searchFieldWrap} ${classes.searchFieldCell} ${classes.lookupFieldStack}`}
                >
                  <Box className={classes.lookupInputRow}>
                    <Box className={classes.lookupHelpIconWrap}>
                      <HelpDialog
                        label={HAKLAI_2990_LABELS.propertyLookup}
                        helpDialog={SMART_ADDRESS_HELP_DIALOG}
                      />
                    </Box>
                    <LabeledTextField
                      type="text"
                      name="parcelAddressSearch"
                      labeledTextFieldDirection="column"
                      placeholder={HAKLAI_2990_SMART_ADDRESS_HELP.placeholder}
                      textFieldWidth={240}
                      value={parcelAddressSearch}
                      disabled={fieldsDisabled}
                      onChange={handleParcelSearchFieldChange}
                      onKeyPress={handleAddressKeyPress}
                    />
                  </Box>
                  <Box className={classes.lookupMessageSlot}>
                    {parcelAddressError ? (
                      <Typography variant="caption" component="div" className={classes.inlineError}>
                        {parcelAddressError}
                      </Typography>
                    ) : (
                      <Typography
                        variant="caption"
                        component="div"
                        aria-hidden="true"
                        className={classes.lookupMessagePlaceholder}
                      >
                        {"\u00A0"}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  className={classes.lookupButton}
                  disabled={fieldsDisabled || addressSearchLoading || !String(parcelAddressSearch || "").trim()}
                  onClick={handleAddressSearchClick}
                >
                  {addressSearchLoading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : HAKLAI_2990_ACTIONS.searchPropertyDetails}
                </Button>
                {hasGovMapAutofill ? (
                  <Typography variant="body2" className={classes.lookupInfoBox}>
                    {HAKLAI_2990_MESSAGES.smartAddressAutofill}
                  </Typography>
                ) : null}
              </Box>

              <Menu
                anchorEl={addressMenuAnchor}
                open={Boolean(addressMenuAnchor) && addressChoices.length > 1}
                onClose={closeAddressParcelMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                getContentAnchorEl={null}
              >
                {addressChoices.map((pair) => (
                  <MenuItem
                    key={`${pair.gush}-${pair.helka}-${pair.objectId != null ? pair.objectId : ""}`}
                    onClick={() => handleAddressChoiceClick(pair)}
                  >
                    {getHaklai2990ParcelChoiceLabel(pair)}
                  </MenuItem>
                ))}
              </Menu>
              {loadError ? (
                <Typography variant="body2" className={classes.lookupError}>
                  {loadError}
                </Typography>
              ) : null}
            </Box>

            <Box className={classes.propertyFieldsBlock}>
              {PROPERTY_FIELD_ROWS.map((fields, index) => (
                <Box
                  key={`property-field-row-${index}`}
                  className={index === 0 ? classes.fieldRow : `${classes.fieldRow} ${classes.gridSection}`}
                >
                  {fields.map((field) => renderDocField(field, classes.fieldCell))}
                </Box>
              ))}
              {parcelPairError ? (
                <Typography variant="body2" className={classes.lookupError}>
                  {parcelPairError}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>

        <Box className={`${classes.section} ${classes.sectionPanel}`}>
          <Box className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {HAKLAI_2990_LABELS.sellerAndRegistration}
            </Typography>
          </Box>

          <Box className={classes.sellerDetailsGrid}>
            <Box className={classes.sellerFieldsBlock}>
              <Box className={classes.sellerFieldRows}>
                {SELLER_FIELD_ROWS.map((fields, index) => (
                  <Box key={`seller-field-row-${index}`} className={classes.sellerFieldLine}>
                    {fields.map((field) => renderDocField(field, classes.sellerFieldCell))}
                  </Box>
                ))}
              </Box>
            </Box>

            <TransferredShareFraction
              classes={classes}
              disabled={fieldsDisabled}
              docFields={docFields}
              fieldErrors={jsonFieldErrors}
              onChange={handleDocFieldChange}
              registerFieldRef={registerFieldRef}
            />
          </Box>
        </Box>

        <Box className={`${classes.section} ${classes.sectionPanel}`}>
          <Box className={classes.actionRow}>
            <Button
              variant="contained"
              color="secondary"
              className={classes.actionButton}
              endIcon={isSaving ? <CircularProgress color="inherit" size={18} /> : <SaveOutlinedIcon />}
              disabled={fieldsDisabled}
              onClick={handleSave}
            >
              {HAKLAI_2990_ACTIONS.savePersonalDetails}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              className={classes.actionButton}
              endIcon={isGeneratingPdf ? <CircularProgress color="inherit" size={18} /> : <DescriptionOutlinedIcon />}
              disabled={fieldsDisabled}
              onClick={handleGeneratePdf}
            >
              {HAKLAI_2990_ACTIONS.generateForm2990}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              className={classes.actionButton}
              endIcon={isGeneratingDeductions ? <CircularProgress color="inherit" size={18} /> : <DescriptionOutlinedIcon />}
              disabled={fieldsDisabled}
              onClick={handleGenerateDeductions}
            >
              {HAKLAI_2990_ACTIONS.generateDeductions}
            </Button>
          </Box>

          <Divider className={classes.compactDivider} />

          <Box className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {HAKLAI_2990_LABELS.onlineFiles}
            </Typography>
          </Box>

          {exportGroups.length === 0 ? (
            <Typography variant="body2" className={classes.jsonEmptyState}>
              {HAKLAI_2990_MESSAGES.exportFilesEmpty}
            </Typography>
          ) : null}

          <Box className={classes.jsonGroupsGrid}>
            {exportGroups.map((group) => (
              <Box key={`json-group-${group.index}`} className={classes.jsonGroup}>
                <Typography variant="body2" className={classes.jsonGroupTitle}>
                  {group.title}
                </Typography>
                <Box className={classes.jsonButtonsRow}>
                  {group.needsMutav ? (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      className={`${classes.actionButton} ${classes.exportButton}`}
                      endIcon={
                        activeJsonButton === `mutav-${group.index}`
                          ? <CircularProgress color="inherit" size={16} />
                          : <GetAppOutlinedIcon />
                      }
                      disabled={fieldsDisabled}
                      onClick={() => handleGenerateJson("mutav", group.index)}
                    >
                      {HAKLAI_2990_ACTIONS.generateMutavFile}
                    </Button>
                  ) : null}
                  {group.needsRegTax ? (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      className={`${classes.actionButton} ${classes.exportButton}`}
                      endIcon={
                        activeJsonButton === `linear-${group.index}`
                          ? <CircularProgress color="inherit" size={16} />
                          : <GetAppOutlinedIcon />
                      }
                      disabled={fieldsDisabled}
                      onClick={() => handleGenerateJson("linear", group.index)}
                    >
                      {HAKLAI_2990_ACTIONS.generateLinearFile}
                    </Button>
                  ) : null}
                  {!group.needsMutav && !group.needsRegTax ? (
                    <Typography variant="body2" className={classes.jsonEmptyState}>
                      {HAKLAI_2990_MESSAGES.noExportRoutesForPart}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default React.memo(HaklaiResults2990Panel);
